import { useEffect, useState } from 'react';
import { Button, Flex } from '@optiaxiom/react';

import { FormInput, PageHeader } from '@frontend/components';
import { useObjectTitle } from '@frontend/hooks';
import { AppPagePadding } from '@frontend/styles';
import { AuthProvider, ToolsManagementPage } from '@frontend/types';

import { useCreateAuthProvider, useUpdateAuthProvider } from '../../hooks';

import { styles } from './AuthProviderForm.css';

type AuthProviderFormProps = {
  setActivePage: (page: ToolsManagementPage) => void;
  provider?: AuthProvider | null;
};

const getInitialFormData = (provider?: AuthProvider | null) => ({
  name: provider?.name ?? '',
  description: provider?.description ?? '',
  auth_type: provider?.auth_type ?? 'oauth2',
  auth_url: provider?.auth_url ?? '',
  token_url: provider?.token_url ?? '',
  revoke_url: provider?.revoke_url ?? '',
  refresh_url: provider?.refresh_url ?? '',
  client_id: provider?.client_id ?? '',
  client_secret: '',
});

export function AuthProviderForm({ setActivePage, provider }: AuthProviderFormProps) {
  const isEditMode = Boolean(provider);
  const [formData, setFormData] = useState(() => getInitialFormData(provider));

  // Set document title for the auth provider
  useObjectTitle(formData.name);

  // Reset form data when provider changes
  useEffect(() => {
    setFormData(getInitialFormData(provider));
  }, [provider]);

  function onSuccess() {
    setFormData(getInitialFormData());
    setActivePage('auth-providers');
  }

  const { createProvider, isCreating } = useCreateAuthProvider(onSuccess);
  const { updateProvider, isUpdating } = useUpdateAuthProvider(onSuccess);

  const isLoading = isCreating || isUpdating;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isEditMode && provider) {
      // For update, only send changed fields
      const updatePayload: Record<string, string | undefined> = {};

      if (formData.description !== provider.description) {
        updatePayload.description = formData.description || undefined;
      }
      if (formData.auth_url !== provider.auth_url) {
        updatePayload.auth_url = formData.auth_url;
      }
      if (formData.token_url !== provider.token_url) {
        updatePayload.token_url = formData.token_url;
      }
      if (formData.revoke_url !== (provider.revoke_url ?? '')) {
        updatePayload.revoke_url = formData.revoke_url || undefined;
      }
      if (formData.refresh_url !== (provider.refresh_url ?? '')) {
        updatePayload.refresh_url = formData.refresh_url || undefined;
      }
      // Only include client_id and client_secret if they were changed
      if (formData.client_id !== provider.client_id) {
        updatePayload.client_id = formData.client_id;
      }
      if (formData.client_secret) {
        updatePayload.client_secret = formData.client_secret;
      }

      updateProvider({ providerId: provider.id, payload: updatePayload });
    } else {
      createProvider({
        name: formData.name,
        description: formData.description || undefined,
        auth_type: formData.auth_type,
        auth_url: formData.auth_url,
        token_url: formData.token_url,
        revoke_url: formData.revoke_url || undefined,
        refresh_url: formData.refresh_url || undefined,
        client_id: formData.client_id,
        client_secret: formData.client_secret,
      });
    }
  };

  // Validation: for edit mode, client_id and client_secret are optional
  const isValid = isEditMode
    ? formData.auth_url && formData.token_url
    : formData.name &&
      formData.auth_url &&
      formData.token_url &&
      formData.client_id &&
      formData.client_secret;

  return (
    <Flex className={`${styles.formContainer} ${AppPagePadding}`} flexDirection="column">
      <PageHeader
        title={isEditMode ? 'Edit Authentication Provider' : 'Add Authentication Provider'}
      />
      <form onSubmit={handleSubmit}>
        <Flex className={styles.formInputsContainer} flexDirection="column">
          <FormInput
            name="name"
            label="Provider Name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="e.g., Google, Microsoft, GitHub"
            required
            disabled={isEditMode}
          />
          <FormInput
            name="description"
            label="Description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="OAuth provider for Google services"
          />
          <FormInput
            name="auth_type"
            label="Auth Type"
            value={formData.auth_type}
            onChange={handleInputChange}
            placeholder="oauth2"
            required
            disabled={isEditMode}
          />
          <FormInput
            name="auth_url"
            label="Authorization URL"
            value={formData.auth_url}
            onChange={handleInputChange}
            placeholder="https://accounts.google.com/o/oauth2/v2/auth"
            required
          />
          <FormInput
            name="token_url"
            label="Token URL"
            value={formData.token_url}
            onChange={handleInputChange}
            placeholder="https://oauth2.googleapis.com/token"
            required
          />
          <FormInput
            name="revoke_url"
            label="Revoke URL (Optional)"
            value={formData.revoke_url}
            onChange={handleInputChange}
            placeholder="https://oauth2.googleapis.com/revoke"
          />
          <FormInput
            name="refresh_url"
            label="Refresh URL (Optional)"
            value={formData.refresh_url}
            onChange={handleInputChange}
            placeholder="https://oauth2.googleapis.com/token"
          />
          <FormInput
            name="client_id"
            label={isEditMode ? 'Client ID (leave unchanged if not updating)' : 'Client ID'}
            value={formData.client_id}
            onChange={handleInputChange}
            placeholder="Enter OAuth client ID"
            required={!isEditMode}
          />
          <FormInput
            name="client_secret"
            label={isEditMode ? 'Client Secret (leave blank to keep current)' : 'Client Secret'}
            value={formData.client_secret}
            onChange={handleInputChange}
            placeholder={
              isEditMode ? 'Enter new secret or leave blank' : 'Enter OAuth client secret'
            }
            type="password"
            required={!isEditMode}
          />
        </Flex>
        <Flex className={styles.buttonContainer} justifyContent="flex-end">
          <Button appearance="subtle" onClick={() => setActivePage('auth-providers')}>
            Cancel
          </Button>
          <Button
            type="submit"
            appearance="primary"
            loading={isLoading}
            disabled={!isValid || isLoading}
          >
            {isEditMode ? 'Update Provider' : 'Save Provider'}
          </Button>
        </Flex>
      </form>
    </Flex>
  );
}
